import type {
  OpenElementDefinition,
  ProfileViewElement,
  ModelViewElement,
  DefinitionNode,
  PropertyDescriptor,
  NodeControlDescriptor,
  ControlSchema,
} from "../elements/generic-element";

// ---------------------------------------------------------------------------
// Static registry: node kind → scalar / color property keys
// ---------------------------------------------------------------------------

type PropertyEntry = { key: string; type: "number" | "color" };

const CUBOID_PROPS: PropertyEntry[] = [
  { key: "width", type: "number" },
  { key: "height", type: "number" },
  { key: "depth", type: "number" },
  { key: "color", type: "color" },
];

const CYLINDER_PROPS: PropertyEntry[] = [
  { key: "radius", type: "number" },
  { key: "height", type: "number" },
  { key: "segments", type: "number" },
  { key: "color", type: "color" },
];

const ARC_PROPS: PropertyEntry[] = [
  { key: "radius", type: "number" },
  { key: "startAngle", type: "number" },
  { key: "endAngle", type: "number" },
  { key: "segments", type: "number" },
  { key: "color", type: "color" },
];

const COLOR_ONLY: PropertyEntry[] = [
  { key: "color", type: "color" },
];

const OFFSET2D_PROPS: PropertyEntry[] = [
  { key: "distance", type: "number" },
];

const KIND_REGISTRY: Record<string, PropertyEntry[]> = {
  cuboid: CUBOID_PROPS,
  cylinder: CYLINDER_PROPS,
  arc: ARC_PROPS,
  polygon: COLOR_ONLY,
  polyline: COLOR_ONLY,
  sweep: COLOR_ONLY,
  offset2d: OFFSET2D_PROPS,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractProperties(node: Record<string, unknown>, entries: PropertyEntry[]): PropertyDescriptor[] {
  const props: PropertyDescriptor[] = [];

  for (const entry of entries) {
    const value = node[entry.key];
    if (value === undefined || value === null) continue;

    if (entry.type === "number" && typeof value === "number") {
      props.push({ key: entry.key, type: "number", value });
    } else if (entry.type === "color" && (typeof value === "string" || typeof value === "number")) {
      props.push({ key: entry.key, type: "color", value });
    }
  }

  return props;
}

function buildNodeDescriptor(
  nodeId: string,
  nodeKind: string,
  section: "profileView" | "modelView" | "definitions",
  node: Record<string, unknown>,
): NodeControlDescriptor | null {
  const entries = KIND_REGISTRY[nodeKind];
  if (!entries) return null;

  const properties = extractProperties(node, entries);
  if (properties.length === 0) return null;

  return { nodeId, nodeKind, section, properties };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildControlSchema(definition: OpenElementDefinition): ControlSchema {
  const nodes: NodeControlDescriptor[] = [];

  // Profile view elements
  if (definition.profileView?.elements) {
    definition.profileView.elements.forEach((element: ProfileViewElement, index: number) => {
      if ("ref" in element) return; // skip RefNodes
      const nodeRecord = element as unknown as Record<string, unknown>;
      const id = element.id ?? `${element.kind}-profile-${index}`;
      const descriptor = buildNodeDescriptor(id, element.kind, "profileView", nodeRecord);
      if (descriptor) nodes.push(descriptor);
    });
  }

  // Model view elements
  if (definition.modelView?.elements) {
    definition.modelView.elements.forEach((element: ModelViewElement, index: number) => {
      if ("ref" in element) return; // skip RefNodes
      const nodeRecord = element as unknown as Record<string, unknown>;
      const id = element.id ?? `${element.kind}-model-${index}`;
      const descriptor = buildNodeDescriptor(id, element.kind, "modelView", nodeRecord);
      if (descriptor) nodes.push(descriptor);
    });
  }

  // Definition nodes
  if (definition.definitions) {
    for (const [key, node] of Object.entries(definition.definitions)) {
      const defNode = node as DefinitionNode;
      const nodeRecord = defNode as unknown as Record<string, unknown>;
      const id = defNode.id ?? key;
      const descriptor = buildNodeDescriptor(id, defNode.kind, "definitions", nodeRecord);
      if (descriptor) nodes.push(descriptor);
    }
  }

  return { nodes };
}
